import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/pensionCalc';

// In-app equivalent of scripts/audit_part_time_pay.sql, so part-time pay can be checked
// and corrected without opening a database console.
//
// Part-time pay derives from the working pattern (contracted hours vs full-time hours).
// Where no pattern is recorded it falls back to the prorata_salary / annual_salary ratio,
// and that fallback is self-cancelling: raise the salary and the fraction shrinks by the
// same proportion, so pay never moves. Recording the pattern is what breaks that loop.

const STATE_META = {
  ok: { label: 'OK', cls: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800' },
  no_pattern: { label: 'No pattern', cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800' },
  underpaid: { label: 'Pay disagrees', cls: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800' },
  stale_display: { label: 'Display stale', cls: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800' },
};

function StateChip({ state }) {
  const meta = STATE_META[state] || STATE_META.ok;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function SummaryTile({ label, value, tone = 'default' }) {
  const tones = {
    default: 'text-gray-900 dark:text-gray-100',
    warn: 'text-amber-700 dark:text-amber-300',
    bad: 'text-red-700 dark:text-red-300',
    good: 'text-green-700 dark:text-green-300',
  };
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums ${tones[tone]}`}>{value}</p>
    </div>
  );
}

// Editor for one employee's pattern. Shows the resulting pay before saving, because this
// changes what the person is paid and that shouldn't be a surprise.
function PatternEditor({ row, onSaved, onCancel }) {
  const [hours, setHours] = useState(row.pt_weekly_hours ?? '');
  const [fteHours, setFteHours] = useState(row.pt_fte_weekly_hours ?? '37.5');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const h = parseFloat(hours) || 0;
  const fh = parseFloat(fteHours) || 0;
  const valid = h > 0 && fh > 0 && h < fh;
  const fraction = valid ? h / fh : null;
  const newProRata = fraction !== null ? row.fte_annual * fraction : null;
  const delta = newProRata !== null ? newProRata / 12 - row.monthly_paid : null;

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await api.patch(`/admin/employees/${row.id}/part-time-pattern`, {
        pt_weekly_hours: h,
        pt_fte_weekly_hours: fh,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the working pattern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-700/40 border-t border-gray-200 dark:border-gray-700 px-4 py-4">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        Record {row.employee}&apos;s contracted hours. Pay is then derived from this, so a
        future salary change reaches their payslip instead of being cancelled out.
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Contracted hours per week
          </label>
          <input type="number" step="0.5" min="0" max="168" inputMode="decimal"
            value={hours} onChange={(e) => { setError(''); setHours(e.target.value); }}
            className="w-40 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="e.g. 30" autoFocus />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Full-time hours for the role
          </label>
          <input type="number" step="0.5" min="0" max="168" inputMode="decimal"
            value={fteHours} onChange={(e) => { setError(''); setFteHours(e.target.value); }}
            className="w-40 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="e.g. 37.5" />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={!valid || saving}
            className="px-3 py-2 text-sm font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save pattern'}
          </button>
        </div>
      </div>

      {valid && (
        <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/30 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-brand-700 dark:text-brand-300">
            After saving, {row.employee.split(' ')[0]} will be paid
          </p>
          <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {formatCurrency(newProRata, { decimals: 'auto' })}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
              ({formatCurrency(newProRata / 12)}/mo)
            </span>
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {Math.round(fraction * 1000) / 10}% of full-time · currently {formatCurrency(row.monthly_paid)}/mo
            {delta !== null && Math.abs(delta) > 0.005 && (
              <span className={delta > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {' '}({delta > 0 ? '+' : ''}{formatCurrency(delta)}/mo)
              </span>
            )}
          </p>
        </div>
      )}

      {!valid && (hours !== '' || fteHours !== '') && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          Contracted hours must be above zero and below the full-time hours — otherwise
          this employee is full-time.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export default function PartTimePayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/part-time-review');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the part-time pay review');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading part-time pay review…</p>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30 px-4 py-3">
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        <button onClick={load} className="mt-2 text-sm font-medium text-red-800 dark:text-red-200 underline">
          Try again
        </button>
      </div>
    );
  }

  const rows = data?.employees || [];
  const s = data?.summary || {};

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-300">
        No part-time employees on record. Nothing to check.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryTile label="Part-time staff" value={s.total ?? 0} />
        <SummaryTile label="No pattern" value={s.no_pattern ?? 0} tone={s.no_pattern ? 'warn' : 'default'} />
        <SummaryTile label="Pay disagrees" value={s.underpaid ?? 0} tone={s.underpaid ? 'bad' : 'default'} />
        <SummaryTile label="Correct" value={s.ok ?? 0} tone={s.ok ? 'good' : 'default'} />
      </div>

      {(s.no_pattern > 0) && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30 px-4 py-3">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            <span className="font-semibold">{s.no_pattern} employee{s.no_pattern === 1 ? '' : 's'} have no working pattern recorded.</span>{' '}
            Their pay still derives from the stored pro-rata ratio, which cancels salary
            changes out — raise the salary and the fraction shrinks by the same amount, so
            the payslip never moves. Record the contracted hours to fix that permanently.
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="px-4 py-2.5 font-semibold">Employee</th>
              <th className="px-4 py-2.5 font-semibold">Company</th>
              <th className="px-4 py-2.5 font-semibold">Pattern</th>
              <th className="px-4 py-2.5 font-semibold text-right">FTE</th>
              <th className="px-4 py-2.5 font-semibold text-right">Pro-rata</th>
              <th className="px-4 py-2.5 font-semibold text-right">Paid /mo</th>
              <th className="px-4 py-2.5 font-semibold">State</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {r.employee}
                    {r.status !== 'active' && (
                      <span className="ml-2 text-xs text-gray-400">({r.status})</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.company_name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 tabular-nums">
                    {r.pt_weekly_hours
                      ? `${r.pt_weekly_hours} / ${r.pt_fte_weekly_hours} hrs`
                      : <span className="text-amber-700 dark:text-amber-300">not recorded</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                    {formatCurrency(r.fte_annual, { decimals: 'auto' })}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                    {r.prorata_annual !== null ? formatCurrency(r.prorata_annual, { decimals: 'auto' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(r.monthly_paid)}
                  </td>
                  <td className="px-4 py-3"><StateChip state={r.state} /></td>
                  <td className="px-4 py-3 text-right">
                    <button type="button"
                      onClick={() => setEditingId(editingId === r.id ? null : r.id)}
                      className="text-sm font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400">
                      {editingId === r.id ? 'Close' : (r.pt_weekly_hours ? 'Edit pattern' : 'Set pattern')}
                    </button>
                  </td>
                </tr>
                {editingId === r.id && (
                  <tr>
                    <td colSpan={8} className="p-0">
                      <PatternEditor
                        row={r}
                        onCancel={() => setEditingId(null)}
                        onSaved={() => { setEditingId(null); load(); }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Paid /mo is the figure payroll will actually use this period, taken from the same
        helper the run sheet uses — not a separate calculation.
      </p>
    </div>
  );
}
